/**
 * Pantalla de Registro de Asistencias
 * 
 * Pantalla para gestionar el registro de asistencias por grupo y fecha.
 * Sigue el patrón unificado del sistema con componentes reutilizables.
 * Reutiliza la lógica de Registro de Calificaciones para obtener grupos del asesor.
 * 
 * Funcionalidades:
 * - Detección automática del asesor autenticado
 * - Obtención de grupos asignados al asesor
 * - Búsqueda de grupos por campus y fecha
 * - Registro de asistencias por estudiante (Presente, Ausente, Tardanza, Justificada)
 * - Historial individual de asistencias por estudiante
 * - Generación de reportes de asistencias
 * 
 * Basado en Figma ID: 4e197578
 * Capturas de referencia: Image 18, 19, 20, 21
 */

import React, { useState, useEffect } from "react";
import styles from "../../styles/CampusStyles/PantallaDeCampus.module.css";
import Sidebar from "../../components/Sidebar";
import HeaderGlobal from "../../components/HeaderGlobal";
import BusquedaGrupo from "./BusquedaGrupo";
import RegistroAsistencia from "./RegistroAsistencia";
import HistorialEstudianteModal from "./HistorialEstudianteModal";
import ReporteAsistenciasModal from "./ReporteAsistenciasModal";
import { getInfoAsesorUsuario } from "../../api/asistencias";

function RegistroAsistenciasScreen() {
  // Eliminado estado de campus seleccionado
  // Estado para vista actual (búsqueda o registro)
  const [vistaActual, setVistaActual] = useState('busqueda'); // 'busqueda' | 'registro'
  
  // Estado para grupo seleccionado
  const [grupoSeleccionado, setGrupoSeleccionado] = useState(null);
  
  // Estado para fecha seleccionada
  const [fechaSeleccionada, setFechaSeleccionada] = useState(() => {
    // Fecha por defecto: hoy en formato YYYY-MM-DD
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  
  // Estado para modal de historial
  const [estudianteHistorial, setEstudianteHistorial] = useState(null);
  
  // Estado para modal de reporte
  const [mostrarReporte, setMostrarReporte] = useState(false);

  // Estado para información del asesor y usuario
  const [infoAsesor, setInfoAsesor] = useState(null);
  const [infoUsuario, setInfoUsuario] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);

  // Cargar información del asesor al montar
  useEffect(() => {
    const cargarInfoAsesor = async () => {
      try {
        setLoadingInfo(true);
        const data = await getInfoAsesorUsuario();
        console.log('[RegistroAsistencias] Info asesor cargada:', data);
        setInfoAsesor(data.asesor || null);
        setInfoUsuario(data.usuario || null);
      } catch (error) {
        console.error('[RegistroAsistencias] Error al cargar info asesor:', error);
      } finally {
        setLoadingInfo(false);
      }
    };
    
    cargarInfoAsesor();
  }, []);

  // Handler para seleccionar un grupo desde la búsqueda
  const handleSeleccionarGrupo = (grupo, fecha) => {
    console.log('[RegistroAsistencias] Grupo seleccionado:', grupo, 'Fecha:', fecha);
    setGrupoSeleccionado(grupo);
    setFechaSeleccionada(fecha);
    setVistaActual('registro');
  };

  // Handler para cambiar fecha desde la captura
  const handleCambioFecha = (nuevaFecha) => {
    console.log('[RegistroAsistencias] Cambio de fecha:', nuevaFecha);
    setFechaSeleccionada(nuevaFecha);
  };

  // Handler para regresar a la búsqueda
  const handleRegresar = () => {
    setVistaActual('busqueda');
    setGrupoSeleccionado(null);
    setFechaSeleccionada(null);
  };

  // Handler para abrir historial de estudiante
  const handleAbrirHistorial = (estudiante) => {
    console.log('[RegistroAsistencias] Abriendo historial de:', estudiante);
    setEstudianteHistorial(estudiante);
  };

  // Handler para cerrar historial
  const handleCerrarHistorial = () => {
    setEstudianteHistorial(null);
  };

  // Handler para abrir modal de reporte
  const handleAbrirReporte = () => {
    setMostrarReporte(true);
  };

  // Handler para cerrar modal de reporte
  const handleCerrarReporte = () => {
    setMostrarReporte(false);
  };

  return (
    <main className={styles.campusScreen}>
      <div className={styles.mainLayout}>
        {/* Sidebar */}
        <aside className={styles.sidebarColumn}>
          <Sidebar />
        </aside>

        <div className={styles.contentColumn}>
          <div className={styles.contentWrapper}>
            {/* Header global reutilizable */}
            <HeaderGlobal title="Registro de Asistencias" />
            {/* Contenido principal */}
            <section className={styles.mainContent}>
              {/* ...se eliminó el selector de institución en esquina superior derecha... */}

              {/* Mensaje de carga */}
              {loadingInfo && (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                  <p>Cargando información del asesor...</p>
                </div>
              )}

              {/* Contenido dinámico según vista actual */}
              {!loadingInfo && (
                <div className={styles.tableSection}>
                  {vistaActual === 'busqueda' ? (
                    <BusquedaGrupo 
                      onSeleccionarGrupo={handleSeleccionarGrupo}
                      infoAsesor={infoAsesor}
                      infoUsuario={infoUsuario}
                    />
                  ) : (
                    <RegistroAsistencia 
                      grupo={grupoSeleccionado}
                      fecha={fechaSeleccionada}
                      onCambioFecha={handleCambioFecha}
                      onRegresar={handleRegresar}
                      onAbrirHistorial={handleAbrirHistorial}
                      onAbrirReporte={handleAbrirReporte}
                    />
                  )}
                </div>
              )}
            </section>

            {/* Modal de Historial de Estudiante */}
            {estudianteHistorial && (
              <HistorialEstudianteModal
                estudiante={estudianteHistorial}
                onCerrar={handleCerrarHistorial}
              />
            )}

            {/* Modal de Reporte de Asistencias */}
            {mostrarReporte && (
              <ReporteAsistenciasModal
                grupo={grupoSeleccionado}
                fecha={fechaSeleccionada}
                infoAsesor={infoAsesor}
                onCerrar={handleCerrarReporte}
              />
            )}
          </div>
        </div>
      </div>
    </main>
  );
}

export default RegistroAsistenciasScreen;
